import React, { useState } from "react";
import { Mail, Globe, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const CONTACT_INFO = [
  { icon: Mail, label: "Email", value: "info@taqaved.com", href: "mailto:info@taqaved.com" },
  { icon: Globe, label: "Website", value: "www.taqaved.com", href: "https://www.taqaved.com" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
    website: "", // honeypot — must remain empty
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    if (!validEmail(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success(
        "Thank you for contacting TAQAVED. Our team will get back to you within one business day."
      );
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        message: "",
        website: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#F4F5F7] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left column: info */}
        <div className="lg:col-span-5">
          <div className="overline mb-5">Contact</div>
          <h2 className="font-display text-4xl md:text-5xl font-medium text-[#0A1128] tracking-tight leading-[1.05]">
            Let&rsquo;s build something
            <br />
            <span className="italic text-[#059669]">investable</span>.
          </h2>
          <p className="mt-6 text-slate-600 text-lg max-w-md">
            Share your opportunity, mandate or partnership idea. We respond within one business
            day.
          </p>

          <div className="mt-12 space-y-6">
            {CONTACT_INFO.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="flex items-start gap-4" data-testid={`contact-info-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#059669]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                      {c.label}
                    </div>
                    {c.href ? (
                      <a
                        href={c.href}
                        className="mt-1 block text-[#0A1128] font-medium hover:text-[#059669] break-words"
                        target={c.href.startsWith("http") ? "_blank" : undefined}
                        rel={c.href.startsWith("http") ? "noreferrer" : undefined}
                        data-testid={`contact-info-link-${i}`}
                      >
                        {c.value}
                      </a>
                    ) : (
                      <div className="mt-1 text-[#0A1128] font-medium">{c.value}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right column: form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="relative bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-[0_20px_60px_-40px_rgba(10,17,40,0.35)]"
          >
            {/* Honeypot — hidden from users, bots will fill it */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={onChange}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field
                label="Name"
                name="name"
                value={form.name}
                onChange={onChange}
                required
                testId="contact-input-name"
                placeholder="Your full name"
              />
              <Field
                label="Company"
                name="company"
                value={form.company}
                onChange={onChange}
                testId="contact-input-company"
                placeholder="Organization"
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                testId="contact-input-email"
                placeholder="you@company.com"
              />
              <Field
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={onChange}
                testId="contact-input-phone"
                placeholder="+966 …"
              />
            </div>

            <div className="mt-5">
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                Message *
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={5}
                placeholder="Tell us about your opportunity, mandate or objective…"
                data-testid="contact-input-message"
                className="mt-2 w-full border border-slate-200 rounded-lg px-4 py-3 text-[15px] focus:outline-none focus:border-[#059669] focus:ring-2 focus:ring-[#059669]/20 resize-none bg-white"
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-slate-500">
                By submitting, you agree to be contacted about your enquiry.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="btn-primary px-7 py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send message
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, required, testId, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 w-full border border-slate-200 rounded-lg px-4 py-3 text-[15px] focus:outline-none focus:border-[#059669] focus:ring-2 focus:ring-[#059669]/20 bg-white"
      />
    </div>
  );
}
